
import React, { useRef, useEffect } from "react";
import MessageItem, { Message } from "./message-item";
import LoadingMessage from "./loading-message";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertCircle, Trash2, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface MessageListProps {
  messages: Message[];
  error?: string | null;
  onClearMessages?: () => void;
  hasPrivacyWarning?: boolean;
  isArchived?: boolean;
  isLoading?: boolean;
}

const MessageList: React.FC<MessageListProps> = ({ 
  messages, 
  error = null,
  onClearMessages,
  hasPrivacyWarning = false,
  isArchived = false,
  isLoading = false
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Enhanced scroll effect that ensures consistent scrolling behavior
  useEffect(() => {
    const scrollToBottom = () => {
      if (bottomRef.current) {
        requestAnimationFrame(() => {
          bottomRef.current?.scrollIntoView({ 
            behavior: "smooth", 
            block: "end" 
          });
        });
      }
    };

    // Always scroll when messages change or when loading state changes
    scrollToBottom();
  }, [messages, isLoading]);

  // Find the last assistant message for placing the warning
  const lastAssistantMessage = messages.filter(m => m.role === 'assistant').slice(-1)[0];
  
  return (
    <ScrollArea ref={scrollAreaRef} className="h-[calc(100vh-16rem)] pb-4">
      <div className="px-4 pt-4">
        <div className="space-y-0">
          {messages.map((message) => (
            <MessageItem key={message.id} message={message} />
          ))}
          
          {/* Show loading message when processing */}
          {isLoading && (
            <LoadingMessage />
          )}
          
          {hasPrivacyWarning && lastAssistantMessage && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Personvernvarsel</AlertTitle>
              <AlertDescription className="mt-2">
                Det ser ut til at samtalen inneholder personopplysninger. For å beskytte personvernet, bør du slette samtalehistorikken.
                <div className="mt-4">
                  <Button 
                    variant="outline" 
                    onClick={onClearMessages}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Slett samtalehistorikk
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {error && (
            <Alert variant="destructive" className="mt-4">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Feil ved kommunikasjon med assistenten</AlertTitle>
              <AlertDescription>
                {error}
                <p className="mt-2">
                  Dette kan være på grunn av endringer i OpenAI-assistenten eller en midlertidig feil.
                </p>
                <div className="mt-4 flex gap-2">
                  <Button 
                    variant="outline" 
                    onClick={onClearMessages}
                    className="bg-soft-blue text-klara-blue hover:bg-blue-100"
                  >
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    Start ny samtale
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    onClick={onClearMessages}
                    className="bg-soft-pink text-red-500 hover:bg-red-100"
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Slett samtalehistorikk
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Always present bottom reference for scrolling */}
          <div ref={bottomRef} className="h-4" />
        </div>
      </div>
    </ScrollArea>
  );
};

export default MessageList;
